import React, { useEffect } from 'react'
import { Modal, Form, Input, Select, Switch, InputNumber } from 'antd'
import type { NotificationTemplate } from '../hooks/useNotificationTemplates'

interface TemplateFormModalProps {
    open: boolean
    onCancel: () => void
    onSubmit: (values: Partial<NotificationTemplate>) => Promise<void>
    initialValues?: NotificationTemplate | null
    loading?: boolean
}

export const TemplateFormModal: React.FC<TemplateFormModalProps> = ({
    open,
    onCancel,
    onSubmit,
    initialValues,
    loading
}) => {
    const [form] = Form.useForm()

    useEffect(() => {
        if (open) {
            if (initialValues) {
                form.setFieldsValue({
                    ...initialValues,
                })
            } else {
                form.resetFields()
                form.setFieldsValue({
                    is_active: true,
                    version: 1,
                    language: 'en',
                    channel: 'sms'
                })
            }
        }
    }, [open, initialValues, form])

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields()
            await onSubmit(values)
            form.resetFields()
        } catch (error) {
            // validation failed
        }
    }

    return (
        <Modal
            title={initialValues ? 'Edit Template' : 'Add New Template'}
            open={open}
            onCancel={onCancel}
            onOk={handleSubmit}
            confirmLoading={loading}
            width={700}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    name="event_name"
                    label="Event Name"
                    rules={[{ required: true, message: 'Please enter the event name' }]}
                >
                    <Input placeholder="e.g. RfqCreated" disabled={!!initialValues} />
                </Form.Item>

                <Form.Item
                    name="channel"
                    label="Channel"
                    rules={[{ required: true, message: 'Please select a channel' }]}
                >
                    <Select disabled={!!initialValues}>
                        <Select.Option value="sms">SMS</Select.Option>
                        <Select.Option value="email">Email</Select.Option>
                        <Select.Option value="whatsapp">WhatsApp</Select.Option>
                        <Select.Option value="push">Push Notification</Select.Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    name="language"
                    label="Language"
                    rules={[{ required: true, message: 'Please select a language' }]}
                >
                    <Select>
                        <Select.Option value="en">English (en)</Select.Option>
                        <Select.Option value="hi">Hindi (hi)</Select.Option>
                    </Select>
                </Form.Item>

                {/* Subject is mostly for email */}
                <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) => prevValues.channel !== currentValues.channel}
                >
                    {({ getFieldValue }) => {
                        const channel = getFieldValue('channel')
                        return channel === 'email' ? (
                            <Form.Item
                                name="subject"
                                label="Subject"
                                rules={[{ required: true, message: 'Please enter a subject' }]}
                            >
                                <Input placeholder="Email Subject" />
                            </Form.Item>
                        ) : null
                    }}
                </Form.Item>

                <Form.Item
                    name="body"
                    label="Message Body"
                    rules={[{ required: true, message: 'Please enter the message body' }]}
                >
                    <Input.TextArea rows={6} placeholder="Use {{ $variable_name }} for dynamic content" />
                </Form.Item>

                <div style={{ display: 'flex', gap: '16px' }}>
                    <Form.Item
                        name="provider"
                        label="Provider"
                        style={{ flex: 1 }}
                    >
                        <Input placeholder="e.g. msg91, aws_ses" />
                    </Form.Item>

                    <Form.Item
                        name="provider_template_id"
                        label="Provider Template ID"
                        style={{ flex: 1 }}
                    >
                        <Input placeholder="If required by provider (e.g. DLT ID)" />
                    </Form.Item>
                </div>

                <div style={{ display: 'flex', gap: '16px' }}>
                    <Form.Item
                        name="version"
                        label="Version"
                        rules={[{ required: true, message: 'Please enter a version number' }]}
                    >
                        <InputNumber min={1} />
                    </Form.Item>

                    <Form.Item
                        name="is_active"
                        label="Active"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>
                </div>

            </Form>
        </Modal>
    )
}
